const config = require("./../../config");
const logger = require("@/utils/logger").default;

let ourSocket;
let reconnectionAttempts = 0;
const reconnectionDelayMin = 1000;  // Minimum delay before attempting reconnection
const reconnectionDelayMax = 1500000;  // Maximum delay to prevent overly long wait times
let reconnectTimeoutId = null;
let currentConnectionId = 0;
let closedByClient = false;

function orderActive(locationId, orderRef) {
  if (ourSocket && ourSocket.readyState === WebSocket.OPEN && locationId && orderRef) {
    ourSocket.send(JSON.stringify({ type: "orderActive", locationId, orderRef }));
  }
}

function orderInactive(locationId, orderRef) {
  if (ourSocket && ourSocket.readyState === WebSocket.OPEN && locationId && orderRef) {
    ourSocket.send(JSON.stringify({ type: "orderInactive", locationId, orderRef }));
  }
}


function initWebSocket(locationId, terminalId, callbacks) {
  const { onOpen, onMessage, onError, onClose } = callbacks;
  const connectionId = ++currentConnectionId;
  closedByClient = false;
  let skipReconnectOnCloseOnce = false;

  function clearReconnectTimeout() {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  }

  function scheduleReconnect(delay) {
    clearReconnectTimeout();
    reconnectTimeoutId = setTimeout(() => {
      if (closedByClient) return;
      if (connectionId !== currentConnectionId) return;
      connect();
    }, delay);
  }

  function connect() {
    if (closedByClient) return;
    if (connectionId !== currentConnectionId) return;

    if (navigator.onLine === false) {
      logger.debug("No internet connection available.");
      scheduleReconnect(reconnectionDelayMin);
      return;
    }

    const protocol =  config.API_HOST === 'localhost' ? 'ws' : 'wss';

    // Prevent duplicate connects if we already have an open socket
    if (
      ourSocket &&
      (ourSocket.readyState === WebSocket.OPEN || ourSocket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const socket = new WebSocket(`${protocol}://${config.API_HOST}${config.API_PORT ? `:${config.API_PORT}` : ""}`);
    ourSocket = socket;

    socket.onopen = () => {
      if (closedByClient) {
        try { socket.close(); } catch (e) { /* noop */ }
        return;
      }
      if (connectionId !== currentConnectionId) {
        try { socket.close(); } catch (e) { /* noop */ }
        return;
      }
      logger.debug("WebSocket connection established");
      subscribeToLocation(locationId, terminalId);
      reconnectionAttempts = 0;
      clearReconnectTimeout();
      if (onOpen) onOpen();
    };

    socket.onmessage = (event) => {
      if (closedByClient) return;
      if (connectionId !== currentConnectionId) return;
      if (isPingMessage(event.data)) {
        handlePing();
      } else {
        if (onMessage) onMessage(event);
      }
    };

    socket.onerror = (error) => {
      if (closedByClient) return;
      if (connectionId !== currentConnectionId) return;
      console.error("WebSocket error:", error);
      if (onError) onError(error);
    };

    socket.onclose = (event) => {
      if (closedByClient) return;
      if (connectionId !== currentConnectionId) return;
      logger.debug("WebSocket closed", { reason: event.reason });
      if (onClose) onClose();
      if (skipReconnectOnCloseOnce) {
        skipReconnectOnCloseOnce = false;
        return;
      }
      attemptReconnect();
    };
  }

  function attemptReconnect() {
    if (closedByClient) return;
    if (connectionId !== currentConnectionId) return;
    let delay = reconnectionDelayMin * Math.pow(2, reconnectionAttempts);
    delay = Math.min(delay, reconnectionDelayMax); // Ensuring delay does not exceed the max
    delay = delay + Math.random() * reconnectionDelayMin; // Adding jitter

    scheduleReconnect(delay);
    reconnectionAttempts++;
    logger.debug(`Attempt ${reconnectionAttempts}: Reconnecting in ${delay} ms`);
  }

  function isPingMessage(message) {
    return message === "ping";
  }

  function handlePing() {
    if (ourSocket && ourSocket.readyState === WebSocket.OPEN) {
      ourSocket.send("pong");
    }
  }

  function subscribeToLocation(locationId, terminalId) {
    if (ourSocket.readyState === WebSocket.OPEN) {
      ourSocket.send(JSON.stringify({ type: "subscribe", locationId, terminalId }));
    }
  }

  function subscribeToOrder(orderId) {
    if (ourSocket && ourSocket.readyState === WebSocket.OPEN && orderId) {
      ourSocket.send(JSON.stringify({ type: "subscribeOrder", orderId }));
    }
  }

  function unsubscribeFromOrder(orderId) {
    if (ourSocket && ourSocket.readyState === WebSocket.OPEN && orderId) {
      ourSocket.send(JSON.stringify({ type: "unsubscribeOrder", orderId }));
    }
  }

  async function manualReconnect() {
    closedByClient = false;
    if (connectionId !== currentConnectionId) return;

    // Close any existing socket to force a clean reconnect.
    if (ourSocket) {
      skipReconnectOnCloseOnce = true;
      try { ourSocket.close(); } catch (e) { /* noop */ }
    }

    reconnectionAttempts = 0;
    clearReconnectTimeout();
    connect();
  }

  function close() {
    // Stop reconnect loops and close the socket for this helper.
    if (connectionId !== currentConnectionId) return;
    closedByClient = true;
    clearReconnectTimeout();
    if (ourSocket) {
      try { ourSocket.close(); } catch (e) { /* noop */ }
    }
  }

  connect();

  return {
    manualReconnect,
    close,
    subscribeToOrder,
    unsubscribeFromOrder
  };
}


export { orderActive, orderInactive, initWebSocket };
